"use client";

import { useTranslations } from "next-intl";
import Image, { StaticImageData } from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Link } from "@/navigation";
import { CaretLeft, CaretRight, ArrowUpRight } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

import "swiper/css";
import "swiper/css/navigation";

import MsAerospaceImg from "@/../public/image/new-version/fabrication/metal-stamping/Customer-Success-Stories/aerospace.webp";
import MsAutomotiveImg from "@/../public/image/new-version/fabrication/metal-stamping/Customer-Success-Stories/automotive.webp";
import MsMedicalDevicesImg from "@/../public/image/new-version/fabrication/metal-stamping/Customer-Success-Stories/medical-devices.webp";
import MsIndustrialTechnologyImg from "@/../public/image/new-version/fabrication/metal-stamping/Customer-Success-Stories/industrial-technology.webp";

import WeldingAerospaceImg from "@/../public/image/new-version/fabrication/metal-welding/SuccessStories/aerospace.webp";
import WeldingRoboticAutomationImg from "@/../public/image/new-version/fabrication/metal-welding/SuccessStories/robotic-automation.webp";
import WeldingIndustrialEquipmentImg from "@/../public/image/new-version/fabrication/metal-welding/SuccessStories/industrial-equipment.webp";
import WeldingAutomotiveImg from "@/../public/image/new-version/fabrication/metal-welding/SuccessStories/automotive.webp";

import CastingAutomotiveImg from "@/../public/image/new-version/casting/Die-Casting/Success-Stories/automotive.webp";
import CastingIndustrialEquipmentImg from "@/../public/image/new-version/casting/Sand-Casting/Success-Stories/industrial-equipment.webp";
import CastingMarineImg from "@/../public/image/new-version/casting/Sand-Casting/Success-Stories/heavy-machinery-industry.webp";

import SandCastingAutomotiveImg from "@/../public/image/new-version/casting/Sand-Casting/Success-Stories/automotive-industry.webp";
import SandCastingHeavyMachineryImg from "@/../public/image/new-version/casting/Sand-Casting/Success-Stories/heavy-machinery-industry.webp";
import SandCastingEnergyImg from "@/../public/image/new-version/casting/Sand-Casting/Success-Stories/energy-sector.webp";
import SandCastingIndustrialEquipmentImg from "@/../public/image/new-version/casting/Sand-Casting/Success-Stories/industrial-equipment.webp";

import HpdcAerospaceImg from "@/../public/image/new-version/casting/High-Pressure-Die-Casting/Success-Stories/aerospace.webp";
import HpdcAutomotiveImg from "@/../public/image/new-version/casting/High-Pressure-Die-Casting/Success-Stories/premium-automotive.webp";
import HpdcElectronicsImg from "@/../public/image/new-version/casting/High-Pressure-Die-Casting/Success-Stories/consumer-electronics.webp";
import HpdcRoboticsImg from "@/../public/image/new-version/casting/High-Pressure-Die-Casting/Success-Stories/Industrial-robotics.webp";
import HpdcMoldsImg from "@/../public/image/new-version/casting/High-Pressure-Die-Casting/Success-Stories/precision-molds.webp";

import LpdcAerospaceImg from "@/../public/image/new-version/casting/Low-Pressure-Die-Casting/Success-Stories/aerospace.webp";
import LpdcAutomotiveImg from "@/../public/image/new-version/casting/Low-Pressure-Die-Casting/Success-Stories/premium-automotive.webp";
import LpdcMedicalImg from "@/../public/image/new-version/casting/Low-Pressure-Die-Casting/Success-Stories/medical-devices.webp";
import LpdcNewEnergyImg from "@/../public/image/new-version/casting/Low-Pressure-Die-Casting/Success-Stories/new-energy.webp";
import LpdcPrecisionInstrumentsImg from "@/../public/image/new-version/casting/Low-Pressure-Die-Casting/Success-Stories/precision-instruments.webp.webp";

import DieCastingAutomotiveImg from "@/../public/image/new-version/casting/Die-Casting/Success-Stories/automotive.webp";
import DieCastingAerospaceImg from "@/../public/image/new-version/casting/Die-Casting/Success-Stories/aerospace.webp";
import DieCastingMedicalImg from "@/../public/image/new-version/casting/Die-Casting/Success-Stories/medical.webp";
import DieCastingElectronicsImg from "@/../public/image/new-version/casting/Die-Casting/Success-Stories/consumer-Electronics.webp";
import SpringMedicalImg from "@/../public/image/new-version/more/SpringManufacturing/CustomerSuccessStories/medical-healthcare.webp";
import SpringConsumerElectronicsImg from "@/../public/image/new-version/more/SpringManufacturing/CustomerSuccessStories/consumer-electronics.webp";
import SpringCollaborativeRoboticsImg from "@/../public/image/new-version/more/SpringManufacturing/CustomerSuccessStories/collaborative-robotics.webp";
import SpringSmartHomeImg from "@/../public/image/new-version/more/SpringManufacturing/CustomerSuccessStories/smart-home.webp";
import SpringNewEnergyImg from "@/../public/image/new-version/more/SpringManufacturing/CustomerSuccessStories/new-energy.webp";

interface MetricData {
	valueKey: string;
	labelKey: string;
}

export interface CaseData {
	id: number;
	categoryKey: string;
	titleKey: string;
	descKey: string;
	metrics: MetricData[];
	image: StaticImageData;
}

const stampingCasesData: CaseData[] = [
	{
		id: 1,
		categoryKey: "successStoriesCase1Category",
		titleKey: "successStoriesCase1Title",
		descKey: "successStoriesCase1Description",
		metrics: [
			{ valueKey: "successStoriesCase1Metric1Value", labelKey: "successStoriesCase1Metric1Label" },
			{ valueKey: "successStoriesCase1Metric2Value", labelKey: "successStoriesCase1Metric2Label" },
			{ valueKey: "successStoriesCase1Metric3Value", labelKey: "successStoriesCase1Metric3Label" },
		],
		image: MsAerospaceImg,
	},
	{
		id: 2,
		categoryKey: "successStoriesCase2Category",
		titleKey: "successStoriesCase2Title",
		descKey: "successStoriesCase2Description",
		metrics: [
			{ valueKey: "successStoriesCase2Metric1Value", labelKey: "successStoriesCase2Metric1Label" },
			{ valueKey: "successStoriesCase2Metric2Value", labelKey: "successStoriesCase2Metric2Label" },
			{ valueKey: "successStoriesCase2Metric3Value", labelKey: "successStoriesCase2Metric3Label" },
		],
		image: MsAutomotiveImg,
	},
	{
		id: 3,
		categoryKey: "successStoriesCase3Category",
		titleKey: "successStoriesCase3Title",
		descKey: "successStoriesCase3Description",
		metrics: [
			{ valueKey: "successStoriesCase3Metric1Value", labelKey: "successStoriesCase3Metric1Label" },
			{ valueKey: "successStoriesCase3Metric2Value", labelKey: "successStoriesCase3Metric2Label" },
			{ valueKey: "successStoriesCase3Metric3Value", labelKey: "successStoriesCase3Metric3Label" },
		],
		image: MsMedicalDevicesImg,
	},
	{
		id: 4,
		categoryKey: "successStoriesCase4Category",
		titleKey: "successStoriesCase4Title",
		descKey: "successStoriesCase4Description",
		metrics: [
			{ valueKey: "successStoriesCase4Metric1Value", labelKey: "successStoriesCase4Metric1Label" },
			{ valueKey: "successStoriesCase4Metric2Value", labelKey: "successStoriesCase4Metric2Label" },
			{ valueKey: "successStoriesCase4Metric3Value", labelKey: "successStoriesCase4Metric3Label" },
		],
		image: MsIndustrialTechnologyImg,
	},
];

const weldingCasesData: CaseData[] = [
	{
		id: 1,
		categoryKey: "successStoriesCase1Category",
		titleKey: "successStoriesCase1Title",
		descKey: "successStoriesCase1Description",
		metrics: [
			{ valueKey: "successStoriesCase1Metric1Value", labelKey: "successStoriesCase1Metric1Label" },
			{ valueKey: "successStoriesCase1Metric2Value", labelKey: "successStoriesCase1Metric2Label" },
			{ valueKey: "successStoriesCase1Metric3Value", labelKey: "successStoriesCase1Metric3Label" },
		],
		image: WeldingAerospaceImg,
	},
	{
		id: 2,
		categoryKey: "successStoriesCase2Category",
		titleKey: "successStoriesCase2Title",
		descKey: "successStoriesCase2Description",
		metrics: [
			{ valueKey: "successStoriesCase2Metric1Value", labelKey: "successStoriesCase2Metric1Label" },
			{ valueKey: "successStoriesCase2Metric2Value", labelKey: "successStoriesCase2Metric2Label" },
			{ valueKey: "successStoriesCase2Metric3Value", labelKey: "successStoriesCase2Metric3Label" },
		],
		image: WeldingRoboticAutomationImg,
	},
	{
		id: 3,
		categoryKey: "successStoriesCase3Category",
		titleKey: "successStoriesCase3Title",
		descKey: "successStoriesCase3Description",
		metrics: [
			{ valueKey: "successStoriesCase3Metric1Value", labelKey: "successStoriesCase3Metric1Label" },
			{ valueKey: "successStoriesCase3Metric2Value", labelKey: "successStoriesCase3Metric2Label" },
			{ valueKey: "successStoriesCase3Metric3Value", labelKey: "successStoriesCase3Metric3Label" },
		],
		image: WeldingIndustrialEquipmentImg,
	},
	{
		id: 4,
		categoryKey: "successStoriesCase4Category",
		titleKey: "successStoriesCase4Title",
		descKey: "successStoriesCase4Description",
		metrics: [
			{ valueKey: "successStoriesCase4Metric1Value", labelKey: "successStoriesCase4Metric1Label" },
			{ valueKey: "successStoriesCase4Metric2Value", labelKey: "successStoriesCase4Metric2Label" },
			{ valueKey: "successStoriesCase4Metric3Value", labelKey: "successStoriesCase4Metric3Label" },
		],
		image: WeldingAutomotiveImg,
	},
];

const metalCastingCasesData: CaseData[] = [
	{
		id: 1,
		categoryKey: "successStoriesCase1Category",
		titleKey: "successStoriesCase1Title",
		descKey: "successStoriesCase1Description",
		metrics: [
			{ valueKey: "successStoriesCase1Metric1Value", labelKey: "successStoriesCase1Metric1Label" },
			{ valueKey: "successStoriesCase1Metric2Value", labelKey: "successStoriesCase1Metric2Label" },
			{ valueKey: "successStoriesCase1Metric3Value", labelKey: "successStoriesCase1Metric3Label" },
		],
		image: CastingAutomotiveImg,
	},
	{
		id: 2,
		categoryKey: "successStoriesCase2Category",
		titleKey: "successStoriesCase2Title",
		descKey: "successStoriesCase2Description",
		metrics: [
			{ valueKey: "successStoriesCase2Metric1Value", labelKey: "successStoriesCase2Metric1Label" },
			{ valueKey: "successStoriesCase2Metric2Value", labelKey: "successStoriesCase2Metric2Label" },
			{ valueKey: "successStoriesCase2Metric3Value", labelKey: "successStoriesCase2Metric3Label" },
		],
		image: CastingIndustrialEquipmentImg,
	},
	{
		id: 3,
		categoryKey: "successStoriesCase3Category",
		titleKey: "successStoriesCase3Title",
		descKey: "successStoriesCase3Description",
		metrics: [
			{ valueKey: "successStoriesCase3Metric1Value", labelKey: "successStoriesCase3Metric1Label" },
			{ valueKey: "successStoriesCase3Metric2Value", labelKey: "successStoriesCase3Metric2Label" },
			{ valueKey: "successStoriesCase3Metric3Value", labelKey: "successStoriesCase3Metric3Label" },
		],
		image: CastingMarineImg,
	},
];

export interface CustomerSuccessStoriesConfig {
	namespace: string;
	casesData: CaseData[];
	titleKey: string;
	descKey: string;
	readFullCaseStudyKey: string;
}

const highPressureCastingCasesData: CaseData[] = [
	{
		id: 1,
		categoryKey: "successStoriesCase1Category",
		titleKey: "successStoriesCase1Title",
		descKey: "successStoriesCase1Description",
		metrics: [
			{ valueKey: "successStoriesCase1Metric1Value", labelKey: "successStoriesCase1Metric1Label" },
			{ valueKey: "successStoriesCase1Metric2Value", labelKey: "successStoriesCase1Metric2Label" },
			{ valueKey: "successStoriesCase1Metric3Value", labelKey: "successStoriesCase1Metric3Label" },
		],
		image: HpdcAerospaceImg,
	},
	{
		id: 2,
		categoryKey: "successStoriesCase2Category",
		titleKey: "successStoriesCase2Title",
		descKey: "successStoriesCase2Description",
		metrics: [
			{ valueKey: "successStoriesCase2Metric1Value", labelKey: "successStoriesCase2Metric1Label" },
			{ valueKey: "successStoriesCase2Metric2Value", labelKey: "successStoriesCase2Metric2Label" },
			{ valueKey: "successStoriesCase2Metric3Value", labelKey: "successStoriesCase2Metric3Label" },
		],
		image: HpdcAutomotiveImg,
	},
	{
		id: 3,
		categoryKey: "successStoriesCase3Category",
		titleKey: "successStoriesCase3Title",
		descKey: "successStoriesCase3Description",
		metrics: [
			{ valueKey: "successStoriesCase3Metric1Value", labelKey: "successStoriesCase3Metric1Label" },
			{ valueKey: "successStoriesCase3Metric2Value", labelKey: "successStoriesCase3Metric2Label" },
			{ valueKey: "successStoriesCase3Metric3Value", labelKey: "successStoriesCase3Metric3Label" },
		],
		image: HpdcElectronicsImg,
	},
	{
		id: 4,
		categoryKey: "successStoriesCase4Category",
		titleKey: "successStoriesCase4Title",
		descKey: "successStoriesCase4Description",
		metrics: [
			{ valueKey: "successStoriesCase4Metric1Value", labelKey: "successStoriesCase4Metric1Label" },
			{ valueKey: "successStoriesCase4Metric2Value", labelKey: "successStoriesCase4Metric2Label" },
			{ valueKey: "successStoriesCase4Metric3Value", labelKey: "successStoriesCase4Metric3Label" },
		],
		image: HpdcRoboticsImg,
	},
	{
		id: 5,
		categoryKey: "successStoriesCase5Category",
		titleKey: "successStoriesCase5Title",
		descKey: "successStoriesCase5Description",
		metrics: [
			{ valueKey: "successStoriesCase5Metric1Value", labelKey: "successStoriesCase5Metric1Label" },
			{ valueKey: "successStoriesCase5Metric2Value", labelKey: "successStoriesCase5Metric2Label" },
			{ valueKey: "successStoriesCase5Metric3Value", labelKey: "successStoriesCase5Metric3Label" },
		],
		image: HpdcMoldsImg,
	},
];

const sandCastingCasesData: CaseData[] = [
	{
		id: 1,
		categoryKey: "successStoriesCase1Category",
		titleKey: "successStoriesCase1Title",
		descKey: "successStoriesCase1Description",
		metrics: [
			{ valueKey: "successStoriesCase1Metric1Value", labelKey: "successStoriesCase1Metric1Label" },
			{ valueKey: "successStoriesCase1Metric2Value", labelKey: "successStoriesCase1Metric2Label" },
			{ valueKey: "successStoriesCase1Metric3Value", labelKey: "successStoriesCase1Metric3Label" },
		],
		image: SandCastingAutomotiveImg,
	},
	{
		id: 2,
		categoryKey: "successStoriesCase2Category",
		titleKey: "successStoriesCase2Title",
		descKey: "successStoriesCase2Description",
		metrics: [
			{ valueKey: "successStoriesCase2Metric1Value", labelKey: "successStoriesCase2Metric1Label" },
			{ valueKey: "successStoriesCase2Metric2Value", labelKey: "successStoriesCase2Metric2Label" },
			{ valueKey: "successStoriesCase2Metric3Value", labelKey: "successStoriesCase2Metric3Label" },
		],
		image: SandCastingHeavyMachineryImg,
	},
	{
		id: 3,
		categoryKey: "successStoriesCase3Category",
		titleKey: "successStoriesCase3Title",
		descKey: "successStoriesCase3Description",
		metrics: [
			{ valueKey: "successStoriesCase3Metric1Value", labelKey: "successStoriesCase3Metric1Label" },
			{ valueKey: "successStoriesCase3Metric2Value", labelKey: "successStoriesCase3Metric2Label" },
			{ valueKey: "successStoriesCase3Metric3Value", labelKey: "successStoriesCase3Metric3Label" },
		],
		image: SandCastingEnergyImg,
	},
	{
		id: 4,
		categoryKey: "successStoriesCase4Category",
		titleKey: "successStoriesCase4Title",
		descKey: "successStoriesCase4Description",
		metrics: [
			{ valueKey: "successStoriesCase4Metric1Value", labelKey: "successStoriesCase4Metric1Label" },
			{ valueKey: "successStoriesCase4Metric2Value", labelKey: "successStoriesCase4Metric2Label" },
			{ valueKey: "successStoriesCase4Metric3Value", labelKey: "successStoriesCase4Metric3Label" },
		],
		image: SandCastingIndustrialEquipmentImg,
	},
];

const lowPressureCastingCasesData: CaseData[] = [
	{
		id: 1,
		categoryKey: "successStoriesCase1Category",
		titleKey: "successStoriesCase1Title",
		descKey: "successStoriesCase1Description",
		metrics: [
			{ valueKey: "successStoriesCase1Metric1Value", labelKey: "successStoriesCase1Metric1Label" },
			{ valueKey: "successStoriesCase1Metric2Value", labelKey: "successStoriesCase1Metric2Label" },
			{ valueKey: "successStoriesCase1Metric3Value", labelKey: "successStoriesCase1Metric3Label" },
		],
		image: LpdcAerospaceImg,
	},
	{
		id: 2,
		categoryKey: "successStoriesCase2Category",
		titleKey: "successStoriesCase2Title",
		descKey: "successStoriesCase2Description",
		metrics: [
			{ valueKey: "successStoriesCase2Metric1Value", labelKey: "successStoriesCase2Metric1Label" },
			{ valueKey: "successStoriesCase2Metric2Value", labelKey: "successStoriesCase2Metric2Label" },
			{ valueKey: "successStoriesCase2Metric3Value", labelKey: "successStoriesCase2Metric3Label" },
		],
		image: LpdcAutomotiveImg,
	},
	{
		id: 3,
		categoryKey: "successStoriesCase3Category",
		titleKey: "successStoriesCase3Title",
		descKey: "successStoriesCase3Description",
		metrics: [
			{ valueKey: "successStoriesCase3Metric1Value", labelKey: "successStoriesCase3Metric1Label" },
			{ valueKey: "successStoriesCase3Metric2Value", labelKey: "successStoriesCase3Metric2Label" },
			{ valueKey: "successStoriesCase3Metric3Value", labelKey: "successStoriesCase3Metric3Label" },
		],
		image: LpdcMedicalImg,
	},
	{
		id: 4,
		categoryKey: "successStoriesCase4Category",
		titleKey: "successStoriesCase4Title",
		descKey: "successStoriesCase4Description",
		metrics: [
			{ valueKey: "successStoriesCase4Metric1Value", labelKey: "successStoriesCase4Metric1Label" },
			{ valueKey: "successStoriesCase4Metric2Value", labelKey: "successStoriesCase4Metric2Label" },
			{ valueKey: "successStoriesCase4Metric3Value", labelKey: "successStoriesCase4Metric3Label" },
		],
		image: LpdcNewEnergyImg,
	},
	{
		id: 5,
		categoryKey: "successStoriesCase5Category",
		titleKey: "successStoriesCase5Title",
		descKey: "successStoriesCase5Description",
		metrics: [
			{ valueKey: "successStoriesCase5Metric1Value", labelKey: "successStoriesCase5Metric1Label" },
			{ valueKey: "successStoriesCase5Metric2Value", labelKey: "successStoriesCase5Metric2Label" },
			{ valueKey: "successStoriesCase5Metric3Value", labelKey: "successStoriesCase5Metric3Label" },
		],
		image: LpdcPrecisionInstrumentsImg,
	},
];

const dieCastingCasesData: CaseData[] = [
	{
		id: 1,
		categoryKey: "successStoriesCase1Category",
		titleKey: "successStoriesCase1Title",
		descKey: "successStoriesCase1Description",
		metrics: [
			{ valueKey: "successStoriesCase1Metric1Value", labelKey: "successStoriesCase1Metric1Label" },
			{ valueKey: "successStoriesCase1Metric2Value", labelKey: "successStoriesCase1Metric2Label" },
			{ valueKey: "successStoriesCase1Metric3Value", labelKey: "successStoriesCase1Metric3Label" },
		],
		image: DieCastingAutomotiveImg,
	},
	{
		id: 2,
		categoryKey: "successStoriesCase2Category",
		titleKey: "successStoriesCase2Title",
		descKey: "successStoriesCase2Description",
		metrics: [
			{ valueKey: "successStoriesCase2Metric1Value", labelKey: "successStoriesCase2Metric1Label" },
			{ valueKey: "successStoriesCase2Metric2Value", labelKey: "successStoriesCase2Metric2Label" },
			{ valueKey: "successStoriesCase2Metric3Value", labelKey: "successStoriesCase2Metric3Label" },
		],
		image: DieCastingAerospaceImg,
	},
	{
		id: 3,
		categoryKey: "successStoriesCase3Category",
		titleKey: "successStoriesCase3Title",
		descKey: "successStoriesCase3Description",
		metrics: [
			{ valueKey: "successStoriesCase3Metric1Value", labelKey: "successStoriesCase3Metric1Label" },
			{ valueKey: "successStoriesCase3Metric2Value", labelKey: "successStoriesCase3Metric2Label" },
			{ valueKey: "successStoriesCase3Metric3Value", labelKey: "successStoriesCase3Metric3Label" },
		],
		image: DieCastingMedicalImg,
	},
	{
		id: 4,
		categoryKey: "successStoriesCase4Category",
		titleKey: "successStoriesCase4Title",
		descKey: "successStoriesCase4Description",
		metrics: [
			{ valueKey: "successStoriesCase4Metric1Value", labelKey: "successStoriesCase4Metric1Label" },
			{ valueKey: "successStoriesCase4Metric2Value", labelKey: "successStoriesCase4Metric2Label" },
			{ valueKey: "successStoriesCase4Metric3Value", labelKey: "successStoriesCase4Metric3Label" },
		],
		image: DieCastingElectronicsImg,
	},
];

const springManufacturingCasesData: CaseData[] = [
	{
		id: 1,
		categoryKey: "successStoriesCase1Category",
		titleKey: "successStoriesCase1Title",
		descKey: "successStoriesCase1Description",
		metrics: [
			{ valueKey: "successStoriesCase1Metric1Value", labelKey: "successStoriesCase1Metric1Label" },
			{ valueKey: "successStoriesCase1Metric2Value", labelKey: "successStoriesCase1Metric2Label" },
			{ valueKey: "successStoriesCase1Metric3Value", labelKey: "successStoriesCase1Metric3Label" },
		],
		image: SpringMedicalImg,
	},
	{
		id: 2,
		categoryKey: "successStoriesCase2Category",
		titleKey: "successStoriesCase2Title",
		descKey: "successStoriesCase2Description",
		metrics: [
			{ valueKey: "successStoriesCase2Metric1Value", labelKey: "successStoriesCase2Metric1Label" },
			{ valueKey: "successStoriesCase2Metric2Value", labelKey: "successStoriesCase2Metric2Label" },
			{ valueKey: "successStoriesCase2Metric3Value", labelKey: "successStoriesCase2Metric3Label" },
		],
		image: SpringConsumerElectronicsImg,
	},
	{
		id: 3,
		categoryKey: "successStoriesCase3Category",
		titleKey: "successStoriesCase3Title",
		descKey: "successStoriesCase3Description",
		metrics: [
			{ valueKey: "successStoriesCase3Metric1Value", labelKey: "successStoriesCase3Metric1Label" },
			{ valueKey: "successStoriesCase3Metric2Value", labelKey: "successStoriesCase3Metric2Label" },
			{ valueKey: "successStoriesCase3Metric3Value", labelKey: "successStoriesCase3Metric3Label" },
		],
		image: SpringCollaborativeRoboticsImg,
	},
	{
		id: 4,
		categoryKey: "successStoriesCase4Category",
		titleKey: "successStoriesCase4Title",
		descKey: "successStoriesCase4Description",
		metrics: [
			{ valueKey: "successStoriesCase4Metric1Value", labelKey: "successStoriesCase4Metric1Label" },
			{ valueKey: "successStoriesCase4Metric2Value", labelKey: "successStoriesCase4Metric2Label" },
			{ valueKey: "successStoriesCase4Metric3Value", labelKey: "successStoriesCase4Metric3Label" },
		],
		image: SpringSmartHomeImg,
	},
	{
		id: 5,
		categoryKey: "successStoriesCase5Category",
		titleKey: "successStoriesCase5Title",
		descKey: "successStoriesCase5Description",
		metrics: [
			{ valueKey: "successStoriesCase5Metric1Value", labelKey: "successStoriesCase5Metric1Label" },
			{ valueKey: "successStoriesCase5Metric2Value", labelKey: "successStoriesCase5Metric2Label" },
			{ valueKey: "successStoriesCase5Metric3Value", labelKey: "successStoriesCase5Metric3Label" },
		],
		image: SpringNewEnergyImg,
	},
];

/** 按 slug 解析案例配置；未实现的 slug 返回 null（不渲染）。后续新页面在此追加 case 即可。 */
export function getCustomerSuccessStoriesConfig(slug?: string): CustomerSuccessStoriesConfig | null {
	switch (slug) {
		case "stamping":
			return {
				namespace: "new-stamping",
				casesData: stampingCasesData,
				titleKey: "successStoriesTitle",
				descKey: "successStoriesSubtitle",
				readFullCaseStudyKey: "successStoriesReadFullCase",
			};
		case "welding":
			return {
				namespace: "new-welding",
				casesData: weldingCasesData,
				titleKey: "successStoriesTitle",
				descKey: "successStoriesSubtitle",
				readFullCaseStudyKey: "successStoriesReadFullCase",
			};
		case "metal-casting":
			return {
				namespace: "new-metal-casting",
				casesData: metalCastingCasesData,
				titleKey: "successStoriesTitle",
				descKey: "successStoriesSubtitle",
				readFullCaseStudyKey: "successStoriesReadFullCase",
			};
		case "high-pressure-casting":
			return {
				namespace: "new-high-pressure-casting",
				casesData: highPressureCastingCasesData,
				titleKey: "successStoriesTitle",
				descKey: "successStoriesSubtitle",
				readFullCaseStudyKey: "successStoriesReadFullCase",
			};
		case "sand-casting":
			return {
				namespace: "new-sand-casting",
				casesData: sandCastingCasesData,
				titleKey: "successStoriesTitle",
				descKey: "successStoriesSubtitle",
				readFullCaseStudyKey: "successStoriesReadFullCase",
			};
		case "low-pressure-casting":
			return {
				namespace: "new-low-pressure-casting",
				casesData: lowPressureCastingCasesData,
				titleKey: "successStoriesTitle",
				descKey: "successStoriesSubtitle",
				readFullCaseStudyKey: "successStoriesReadFullCase",
			};
		case "die-casting":
			return {
				namespace: "new-die-casting",
				casesData: dieCastingCasesData,
				titleKey: "successStoriesTitle",
				descKey: "successStoriesSubtitle",
				readFullCaseStudyKey: "successStoriesReadFullCase",
			};
		case "spring-manufacturing":
			return {
				namespace: "new-spring-manufacturing",
				casesData: springManufacturingCasesData,
				titleKey: "successStoriesTitle",
				descKey: "successStoriesSubtitle",
				readFullCaseStudyKey: "successStoriesReadFullCase",
			};
		default:
			return null;
	}
}

function MetricCard({
	metric,
	t,
	highlight,
}: {
	metric: MetricData;
	t: (key: string) => string;
	highlight?: boolean;
}) {
	return (
		<div
			className={cn(
				"flex flex-col items-center rounded-lg border-2 px-3 py-4 text-center sm:px-4 sm:py-5",
				highlight ? "border-[#64a525] bg-[#64a525]" : "border-[#64a525] bg-white",
			)}
		>
			<span className={cn("mb-1 text-lg font-bold sm:text-xl md:text-2xl", highlight ? "text-white" : "text-black")}>
				{t(metric.valueKey)}
			</span>
			<span className={cn("text-xs sm:text-sm", highlight ? "text-white/90" : "text-gray-900")}>
				{t(metric.labelKey)}
			</span>
		</div>
	);
}

function CustomerSuccessStoriesSection({
	config,
	className,
}: {
	config: CustomerSuccessStoriesConfig;
	className?: string;
}) {
	const t = useTranslations(config.namespace);
	const casesData = config.casesData;
	const highlightLastMetric = config.namespace === "new-spring-manufacturing";

	const swiperRef = useRef<SwiperType | null>(null);
	const [isBeginning, setIsBeginning] = useState(true);
	const [isEnd, setIsEnd] = useState(false);

	const handleSlideChange = (swiper: SwiperType) => {
		setIsBeginning(swiper.isBeginning);
		setIsEnd(swiper.isEnd);
	};

	return (
		<section className={cn("bg-[#ffffff] py-8 sm:py-10 md:py-12 lg:py-16", className)}>
			<div className="container">
				<div className="mx-auto mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t(config.titleKey)}
					</h2>
					<p className="mx-auto text-sm leading-relaxed text-gray-900 md:text-base">{t(config.descKey)}</p>
				</div>

				<div className="relative">
					<button
						onClick={() => swiperRef.current?.slidePrev()}
						disabled={isBeginning}
						className={`absolute -left-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center transition-all duration-300 sm:-left-4 md:-left-6 lg:-left-10 xl:-left-12 ${
							isBeginning ? "cursor-not-allowed opacity-30" : "cursor-pointer opacity-100 hover:opacity-70"
						}`}
						aria-label="Previous slide"
					>
						<CaretLeft size={28} weight="bold" className="text-[#64a525] sm:h-8 sm:w-8 md:h-9 md:w-9" />
					</button>

					<button
						onClick={() => swiperRef.current?.slideNext()}
						disabled={isEnd}
						className={`absolute -right-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center transition-all duration-300 sm:-right-4 md:-right-6 lg:-right-10 xl:-right-12 ${
							isEnd ? "cursor-not-allowed opacity-30" : "cursor-pointer opacity-100 hover:opacity-70"
						}`}
						aria-label="Next slide"
					>
						<CaretRight size={28} weight="bold" className="text-[#64a525] sm:h-8 sm:w-8 md:h-9 md:w-9" />
					</button>

					<Swiper
						modules={[Navigation]}
						spaceBetween={24}
						slidesPerView={1}
						onSwiper={(swiper) => {
							swiperRef.current = swiper;
						}}
						onSlideChange={handleSlideChange}
						className="overflow-hidden"
					>
						{casesData.map((caseItem) => (
							<SwiperSlide key={caseItem.id}>
								<article className="overflow-hidden rounded-xl border-4 border-[#e5e5e5] bg-white px-4 py-8 shadow-sm transition-shadow duration-300 hover:shadow-xl sm:rounded-2xl sm:border-[6px] sm:px-6 sm:py-10 md:border-[8px] md:px-8 md:py-12 lg:px-10 lg:py-[50px]">
									<div className="flex flex-col gap-6 sm:gap-8 md:gap-10 lg:flex-row">
										<div className="relative aspect-[700/560] w-full overflow-hidden bg-gradient-to-br from-gray-100 to-gray-50 lg:aspect-[700/560] lg:w-[45%]">
											<Image
												src={caseItem.image}
												alt={t(caseItem.titleKey)}
												fill
												className="object-cover"
												sizes="(max-width: 1024px) 100vw, 45vw"
											/>
										</div>

										<div className="flex flex-1 flex-col justify-between">
											<div className="mb-3 sm:mb-4">
												<span className="text-sm font-semibold text-black underline decoration-[#64a525] decoration-2 underline-offset-[5px] sm:text-base">
													{t(caseItem.categoryKey)}
												</span>
											</div>

											<h3 className="mb-2 text-lg font-bold text-black sm:mb-3 sm:text-xl md:text-2xl">
												{t(caseItem.titleKey)}
											</h3>

											<p className="mb-6 text-sm leading-relaxed text-black sm:mb-8 md:text-base">
												{t(caseItem.descKey)}
											</p>

											<div className="mb-6 grid grid-cols-3 gap-2 sm:mb-8 sm:gap-4">
												{caseItem.metrics.map((metric, index) => (
													<MetricCard
														key={index}
														metric={metric}
														t={t}
														highlight={highlightLastMetric && index === caseItem.metrics.length - 1}
													/>
												))}
											</div>

											<Link
												href="/cases"
												className="inline-flex items-center text-sm font-semibold text-[#64a525] transition-colors hover:text-[#83b751] sm:text-base"
											>
												{t(config.readFullCaseStudyKey)}
												<ArrowUpRight size={18} weight="bold" />
											</Link>
										</div>
									</div>
								</article>
							</SwiperSlide>
						))}
					</Swiper>
				</div>
			</div>
		</section>
	);
}

interface CustomerSuccessStoriesProps {
	slug?: string;
	className?: string;
}

export default function CustomerSuccessStories({ slug, className }: CustomerSuccessStoriesProps) {
	const config = getCustomerSuccessStoriesConfig(slug);
	if (!config) return null;
	return <CustomerSuccessStoriesSection config={config} className={className} />;
}
